import { useEffect, useRef, useState } from 'react';
import { LogOut } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Session } from '@product-portal/shared';
import { apiFetch } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/labels';
import { initials } from '@/lib/utils';

export async function signOut(queryClient: ReturnType<typeof useQueryClient>) {
  await apiFetch('/api/auth/logout', { method: 'POST' });
  queryClient.clear();
  window.location.assign('/connexion');
}

/**
 * Avatar of the header. The mock-ups show the avatar only; the menu holding
 * « Se déconnecter » is not drawn in them (see CLAUDE.md, « Écarts »).
 */
export function UserMenu({ session }: { session: Session }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const fullName = `${session.user.firstName} ${session.user.lastName}`.trim();

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={fullName}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex size-9 cursor-pointer items-center justify-center rounded-full bg-avatar text-[13px] font-bold text-avatar-foreground"
      >
        {initials(session.user.firstName, session.user.lastName)}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-11 z-10 flex min-w-60 flex-col gap-1 rounded-card border border-border bg-popover p-3 shadow-lift">
          <span className="font-bold">{fullName}</span>
          <span className="text-[13px] text-muted-foreground">{session.user.email}</span>
          <span className="text-[13px] text-muted-foreground">{ROLE_LABELS[session.role]}</span>
          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut(queryClient)}
            className="mt-2 flex min-h-10 cursor-pointer items-center gap-2 rounded-control px-2 text-left text-sm text-foreground-secondary hover:bg-accent"
          >
            <LogOut aria-hidden="true" className="size-4" />
            Se déconnecter
          </button>
        </div>
      )}
    </div>
  );
}
